import { UMButton, UMCheckbox, UMFormGroup, UMIcon, UMInput, UMSegmentedControl } from '../../urbanmap/_components';
import { fmt } from './domain';
import { PerimeterMap } from './PerimeterMap';
import { PC, UMR, caption, card } from './tokens';
import type { LngLat, PlanBuilding, Referential } from './types';

export type PerimeterMode = 'communes' | 'drawn';

export interface PerimeterDraft {
  name: string;
  mode: PerimeterMode;
  communes: string[];
  points: LngLat[];
  closed: boolean;
  drawing: boolean;
}

interface Props {
  referential: Referential;
  draft: PerimeterDraft;
  onChange: (patch: Partial<PerimeterDraft>) => void;
  included: PlanBuilding[];
  includedIds: Set<string>;
}

const TABS: Record<PerimeterMode, string> = { communes: 'Communes', drawn: 'Dessiner' };

export function StepPerimeter({ referential, draft, onChange, included, includedIds }: Props) {
  const countByCommune = (insee: string) => referential.buildings.filter((b) => b.commune === insee).length;
  const outlines: LngLat[][] = draft.mode === 'communes'
    ? draft.communes.map((c) => referential.communeOutlines[c]).filter((r): r is LngLat[] => !!r)
    : [];
  const perimeterReady = draft.mode === 'communes' ? draft.communes.length > 0 : draft.closed;
  const empty = perimeterReady && included.length === 0;

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ width: 360, minWidth: 360, padding: 24, display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', borderRight: `1px solid ${UMR.border}` }}>
        <UMFormGroup label="Nom du projet">
          <UMInput value={draft.name} onChange={(name) => onChange({ name })} placeholder="Ex. Gentilly — extension réseau 2028" ariaLabel="Nom du projet" />
          {!draft.name.trim() && <div style={{ ...caption, marginTop: 4 }}>Obligatoire pour passer à l’étape suivante.</div>}
        </UMFormGroup>

        <UMFormGroup label="Périmètre géographique">
          <UMSegmentedControl
            options={Object.values(TABS)}
            value={TABS[draft.mode]}
            onChange={(v) => onChange({ mode: v === TABS.drawn ? 'drawn' : 'communes', drawing: false })}
          />
        </UMFormGroup>

        {draft.mode === 'communes' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={caption}>Sélectionnez une ou plusieurs communes : tous leurs bâtiments UrbanMap seront copiés.</div>
            <div style={{ border: `1px solid ${UMR.borderInput}`, padding: '4px 8px' }}>
              {referential.communes.map((c) => {
                const n = countByCommune(c.insee);
                const checked = draft.communes.includes(c.insee);
                return (
                  <div key={c.insee} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${UMR.borderSubtle}` }}>
                    <UMCheckbox
                      checked={checked}
                      onChange={(on) => onChange({ communes: on ? [...draft.communes, c.insee] : draft.communes.filter((x) => x !== c.insee) })}
                      label={`${c.name} (${c.postalCode})`}
                    />
                    <span style={{ ...caption, whiteSpace: 'nowrap' }}>{c.available ? `${fmt(n)} bât.` : 'hors démo'}</span>
                  </div>
                );
              })}
            </div>
            <div style={caption}>Démo : seuls les bâtiments de Gentilly sont disponibles.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ ...card, background: UMR.grey100, fontSize: 13, lineHeight: '19px' }}>
              {draft.drawing
                ? <>Cliquez sur la carte pour ajouter des sommets. <strong>Double-cliquez</strong> ou cliquez sur le premier sommet pour fermer le contour.</>
                : draft.closed
                  ? <>Contour fermé ({draft.points.length} sommets). Seuls les bâtiments dont le centre est à l’intérieur sont copiés.</>
                  : <>Dessinez le contour du quartier à étudier directement sur la carte.</>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {!draft.drawing && (
                <UMButton icon="draw" onClick={() => onChange({ drawing: true, points: [], closed: false })}>
                  {draft.closed || draft.points.length ? 'Redessiner' : 'Dessiner un contour'}
                </UMButton>
              )}
              {draft.drawing && (
                <UMButton icon="check" disabled={draft.points.length < 3} onClick={() => onChange({ drawing: false, closed: true })}>Terminer</UMButton>
              )}
              <UMButton variant="secondary" icon="delete" disabled={draft.points.length === 0} onClick={() => onChange({ points: [], closed: false, drawing: false })}>Effacer</UMButton>
            </div>
            {draft.drawing && <div style={caption}>{draft.points.length} sommet{draft.points.length > 1 ? 's' : ''} · 3 minimum</div>}
          </div>
        )}

        <div
          role="status"
          style={{
            ...card, display: 'flex', alignItems: 'center', gap: 12,
            background: empty ? PC.dangerBg : perimeterReady ? PC.accentBg : UMR.grey100,
            borderColor: empty ? PC.dangerBorder : perimeterReady ? PC.accentBorder : UMR.border,
          }}
        >
          <UMIcon name={empty ? 'error' : 'apartment'} size={28} color={empty ? PC.dangerText : perimeterReady ? PC.accent : UMR.textSecondary} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 20, lineHeight: '28px', color: empty ? PC.dangerText : UMR.textStrong }}>{fmt(included.length)} bâtiments inclus</div>
            <div style={caption}>
              {empty ? 'Aucun bâtiment UrbanMap dans ce périmètre : modifiez-le.'
                : perimeterReady ? 'Copiés depuis UrbanMap à la création (ID UM conservés).'
                  : draft.mode === 'communes' ? 'Sélectionnez au moins une commune.' : 'Dessinez et fermez un contour.'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <PerimeterMap
          buildings={referential.buildings}
          included={includedIds}
          bounds={referential.bounds}
          outlines={outlines}
          drawing={draft.mode === 'drawn' && draft.drawing}
          points={draft.mode === 'drawn' ? draft.points : []}
          closed={draft.closed}
          onAddPoint={(p) => onChange({ points: [...draft.points, p] })}
          onFinish={(points) => onChange(points.length >= 3 ? { points, drawing: false, closed: true } : { points })}
        />
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 5, ...card, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <Legend color={PC.included} label="Inclus dans le projet" />
          <Legend color={PC.excluded} label="Bâtiment UrbanMap hors périmètre" />
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 14, height: 10, background: color, opacity: 0.8 }} />
      {label}
    </span>
  );
}
